import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined'
import CallMergeOutlinedIcon from '@mui/icons-material/CallMergeOutlined'
import CallSplitOutlinedIcon from '@mui/icons-material/CallSplitOutlined'
import InventoryOutlinedIcon from '@mui/icons-material/InventoryOutlined'
import MoreHorizOutlinedIcon from '@mui/icons-material/MoreHorizOutlined'
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined'
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined'
import { Box, Button, Chip, IconButton, Link, Typography } from '@mui/material'
import { ReactNode } from 'react'
import ReactMarkdown from 'react-markdown'
import { useNavigate } from 'react-router-dom'
import remarkGfm from 'remark-gfm'

import { LANGUAGE_KEY } from '@/context/Locale/Locale'
import { useAppContext } from '@/hooks'
import { catalogCanvasSx } from '@/styles/catalogUi'

import { BranchRecord, getBranchesPath } from '../types'
import { BranchDetailCompare } from './BranchDetailCompare'

type BranchDetailProps = {
	activeBranchId: string | null
	branch: BranchRecord | null
	branches: BranchRecord[]
	compareTargetId?: string | null
	onCreateMerge: () => void
	onOpenMenu: (anchor: HTMLElement, branch: BranchRecord) => void
	onUpdateDeploymentPackage: () => void
	updatingDeploymentPackage: boolean
}

const statusMeta: Record<
	BranchRecord['status'],
	{ background: string; color: string; labelKey: LANGUAGE_KEY }
> = {
	ACTIVE: {
		background: 'rgba(55,180,121,0.12)',
		color: '#1E8A55',
		labelKey: 'BRANCHES_OPENED',
	},
	ARCHIVED: {
		background: '#ECECEC',
		color: '#7A7A7A',
		labelKey: 'BRANCHES_STATUS_ARCHIVED',
	},
	DELETED: {
		background: '#FDECEA',
		color: '#C62828',
		labelKey: 'BRANCHES_STATUS_DELETED',
	},
	LOCKED: {
		background: '#FEF2DC',
		color: '#C26401',
		labelKey: 'BRANCHES_STATUS_LOCKED',
	},
}

const badgeSx = (color: string, background: string) => ({
	height: 20,
	borderRadius: '6px',
	bgcolor: background,
	color,
	fontSize: 10,
	fontWeight: 700,
	letterSpacing: '0.06em',
	textTransform: 'uppercase',
	'& .MuiChip-label': { px: '7px', textTransform: 'uppercase' },
	'& .MuiChip-icon': { color, fontSize: 12, ml: '6px' },
})

const actionButtonSx = {
	borderColor: '#1F4954',
	color: '#1F4954',
	borderRadius: '4px',
	textTransform: 'none',
	fontWeight: 600,
	'&:hover': { borderColor: '#1F4954', bgcolor: 'rgba(31,73,84,0.05)' },
}

const MetaCell = ({
	children,
	label,
}: {
	children: ReactNode
	label: string
}) => (
	<Box
		sx={{
			px: 2,
			py: 1.5,
			display: 'flex',
			flexDirection: 'column',
			gap: 0.5,
			borderRight: '1px solid #E6E8EA',
			minWidth: 0,
		}}
	>
		<Typography
			sx={{
				fontSize: 10,
				fontWeight: 700,
				letterSpacing: '0.06em',
				textTransform: 'uppercase',
				color: '#6A6A6A',
			}}
		>
			{label}
		</Typography>
		{children}
	</Box>
)

export const BranchDetail = ({
	activeBranchId,
	branch,
	branches,
	compareTargetId = null,
	onCreateMerge,
	onOpenMenu,
	onUpdateDeploymentPackage,
	updatingDeploymentPackage,
}: BranchDetailProps) => {
	const { t } = useAppContext()
	const navigate = useNavigate()

	if (!branch) {
		return (
			<Box
				sx={{
					...catalogCanvasSx,
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
					color: '#A8B4B8',
					fontSize: 13,
				}}
			>
				{t('BRANCHES_SELECT_HINT')}
			</Box>
		)
	}

	const status = statusMeta[branch.status]
	const isWorkingBranch = activeBranchId === branch.id
	const sourceLabel =
		branch.sourceBranchName ??
		(branch.sourceBranchId == null
			? t('BRANCHES_ROOT_SOURCE')
			: branch.sourceBranchId)
	const description = branch.description.trim()
	const deleted = branch.status === 'DELETED'

	const authored = (when: string, who: string) => (
		<Typography sx={{ fontSize: 14, color: '#222' }}>
			{when}
			{who ? (
				<Box component="span" sx={{ color: '#6A6A6A' }}>
					{` ${t('BRANCHES_META_BY', [who])}`}
				</Box>
			) : null}
		</Typography>
	)

	return (
		<Box
			sx={{
				...catalogCanvasSx,
				display: 'flex',
				flexDirection: 'column',
				overflow: 'hidden',
			}}
			data-testid="branch-detail"
		>
			<Box
				sx={{
					px: 1.5,
					pt: 2,
					pb: 1,
					display: 'flex',
					flexDirection: 'column',
					gap: 2.5,
					flex: 1,
					minHeight: 0,
					overflow: 'hidden',
				}}
			>
				<Typography sx={{ fontSize: 12.5, color: '#6A6A6A' }}>
					{branch.system}
					{' › '}
					{t('ACTIVITY_BAR_BRANCHES')}
					{' › '}
					<Box component="span" sx={{ color: '#1F4954', fontWeight: 700 }}>
						{branch.name}
					</Box>
				</Typography>

				<Box
					sx={{
						bgcolor: '#fff',
						border: '1px solid #E6E8EA',
						borderRadius: '14px',
						overflow: 'hidden',
					}}
				>
					<Box
						sx={{
							display: 'flex',
							alignItems: 'flex-start',
							gap: 1.75,
							px: 2,
							py: 1.75,
							flexWrap: 'wrap',
						}}
					>
						<Box
							sx={{
								width: 40,
								height: 40,
								borderRadius: '8px',
								display: 'flex',
								alignItems: 'center',
								justifyContent: 'center',
								flex: 'none',
								bgcolor: status.background,
								color: status.color,
							}}
						>
							<AccountTreeOutlinedIcon sx={{ fontSize: 22 }} />
						</Box>
						<Box
							sx={{
								flex: 1,
								minWidth: 240,
								display: 'flex',
								flexDirection: 'column',
								gap: 0.5,
							}}
						>
							<Box
								sx={{
									display: 'flex',
									alignItems: 'center',
									gap: 1.25,
									flexWrap: 'wrap',
								}}
							>
								<Typography
									component="h1"
									sx={{
										fontSize: 26,
										fontWeight: 800,
										letterSpacing: '-0.01em',
										lineHeight: 1.1,
										color: '#1F4954',
									}}
								>
									{branch.name}
								</Typography>
								<Chip
									label={t(status.labelKey)}
									sx={badgeSx(status.color, status.background)}
								/>
								{isWorkingBranch ? (
									<Box component="span" data-testid="branches-current-badge">
										<Chip
											label={t('BRANCHES_CURRENT')}
											sx={badgeSx('#1E8A55', 'rgba(55,180,121,0.12)')}
										/>
									</Box>
								) : null}
								{branch.protectedBranch ? (
									<Chip
										icon={<ShieldOutlinedIcon />}
										label={t('BRANCHES_PROTECTED')}
										sx={badgeSx('#5B43C4', '#EEEAFA')}
									/>
								) : null}
								{branch.restrictToExplicitPermissions ? (
									<Chip
										icon={<VisibilityOffOutlinedIcon />}
										label={t('BRANCHES_CONFIDENTIAL_BADGE')}
										sx={badgeSx('#7A7A7A', '#EDEDED')}
									/>
								) : null}
							</Box>
							{description ? (
								<Box
									data-testid="branch-detail-description"
									sx={{
										fontSize: 14,
										color: '#444',
										lineHeight: 1.5,
										'& p': { my: 0 },
										'& a': { color: '#1F4954' },
									}}
								>
									<ReactMarkdown
										remarkPlugins={[remarkGfm]}
										components={{
											a: ({ href, children }) => (
												<a
													href={href}
													rel="noopener noreferrer"
													target="_blank"
												>
													{children}
												</a>
											),
										}}
									>
										{description}
									</ReactMarkdown>
								</Box>
							) : null}
						</Box>
						<Box
							sx={{
								display: 'flex',
								alignItems: 'center',
								gap: 1,
								flexWrap: 'wrap',
							}}
						>
							<Button
								variant="outlined"
								startIcon={<CallMergeOutlinedIcon />}
								disabled={deleted}
								onClick={onCreateMerge}
								data-testid="branches-button-create-merge-request"
								sx={actionButtonSx}
							>
								{t('MERGE_REQUESTS_CREATE')}
							</Button>
							<Button
								variant="outlined"
								startIcon={<InventoryOutlinedIcon />}
								disabled={updatingDeploymentPackage || deleted}
								onClick={onUpdateDeploymentPackage}
								data-testid="branches-button-update-deployment-package"
								sx={actionButtonSx}
							>
								{t('BRANCHES_UPDATE_DEPLOYMENT_PACKAGE')}
							</Button>
							<IconButton
								onClick={(event) => onOpenMenu(event.currentTarget, branch)}
								data-testid="branch-detail-menu"
								sx={{ color: '#1F4954' }}
							>
								<MoreHorizOutlinedIcon />
							</IconButton>
						</Box>
					</Box>

					<Box
						sx={{
							display: 'grid',
							gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
							borderTop: '1px solid #E6E8EA',
							bgcolor: '#FBFBFC',
						}}
					>
						<MetaCell label={t('BRANCHES_SOURCE')}>
							{branch.sourceBranchId ? (
								<Link
									component="button"
									data-testid="branch-detail-parent"
									onClick={() =>
										navigate(getBranchesPath(branch.sourceBranchId as string))
									}
									sx={{
										display: 'inline-flex',
										alignItems: 'center',
										gap: 0.5,
										fontSize: 16,
										fontWeight: 700,
										textAlign: 'left',
									}}
								>
									<CallSplitOutlinedIcon sx={{ fontSize: 16 }} />
									{sourceLabel}
								</Link>
							) : (
								<Typography
									data-testid="branch-detail-parent"
									sx={{ fontSize: 16, fontWeight: 700, color: '#1F4954' }}
								>
									{sourceLabel}
								</Typography>
							)}
						</MetaCell>
						{branch.requiredApprovals != null ? (
							<MetaCell label={t('BRANCHES_REQUIRED_APPROVALS')}>
								<Typography
									sx={{ fontSize: 16, fontWeight: 700, color: '#1F4954' }}
								>
									{branch.requiredApprovals}
								</Typography>
							</MetaCell>
						) : null}
						<MetaCell label={t('BRANCHES_CREATED_AT')}>
							{authored(branch.createdAt, branch.createdBy)}
						</MetaCell>
						<MetaCell label={t('BRANCHES_UPDATED_AT')}>
							{authored(branch.updatedAt, branch.updatedBy)}
						</MetaCell>
					</Box>
				</Box>

				<BranchDetailCompare
					key={branch.id}
					branch={branch}
					branches={branches}
					requestedTargetId={compareTargetId}
				/>
			</Box>
		</Box>
	)
}
